import { z } from 'zod';
import type { TFunction } from 'i18next';

/**
 * Validates the client member invitation form, requiring only an email address.
 *
 * Unlike features/members/, this schema omits role and expiresAt validation
 * since they are not part of the client member invitation flow.
 *
 * @param t - i18next translation function for resolving error message keys
 *
 * @remarks
 * - Role is not validated here because it is always 'client' for client members,
 *   assigned automatically in the invite dialog, not in the form.
 * - Error messages use the 'client-members:errors.*' i18n namespace.
 *
 * @example
 * const schema = createInviteClientMemberSchema(t);
 * const form = useForm<InviteFormValues>({
 *   resolver: zodResolver(schema),
 *   defaultValues: { email: '' },
 * });
 */
export const createInviteClientMemberSchema = (t: TFunction) =>
  z.object({
    email: z.email({ error: t('errors.emailInvalid') }),
  });

/** Inferred form values for the client member invitation form. */
export type InviteFormValues = z.infer<ReturnType<typeof createInviteClientMemberSchema>>;
